import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/Button/Button';
import { detectPlatform, type DetectedPlatform } from './shipDialogPlatform';
import './ShipDialog.css';

/**
 * Ship-to-phone dialog. The kid clicks "Ship to phone" → this dialog
 * walks them through (a) downloading the zip, then (b) the
 * platform-specific install steps. We show all three platforms so the
 * kid + parent + teacher all see what to do, but we highlight the
 * detected one for whichever device the studio is open on.
 *
 * No server is involved. The zip is the deliverable; getting it onto
 * the kid's phone is a manual move (AirDrop / email / Drive / USB).
 * That's the v1 tradeoff per PLAN.md M4 — a hosted install flow
 * arrives in Phase 3 with accounts.
 */

export interface ShipDialogProps {
  isOpen: boolean;
  /** Project name — shown in the title and the manifest copy. */
  projectName: string;
  onClose: () => void;
  /** Resolves once the browser has been handed the zip. The dialog
   *  flips into the post-download install instructions when this
   *  resolves. */
  onDownload: () => Promise<void>;
  /** Override for tests. Production picks based on `navigator.userAgent`. */
  detectedPlatform?: DetectedPlatform;
}

type Status = 'idle' | 'downloading' | 'downloaded' | 'error';

export function ShipDialog({
  isOpen,
  projectName,
  onClose,
  onDownload,
  detectedPlatform = detectPlatform(),
}: ShipDialogProps): JSX.Element | null {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setError(null);
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDownload = async (): Promise<void> => {
    if (status === 'downloading') return;
    setError(null);
    setStatus('downloading');
    try {
      await onDownload();
      setStatus('downloaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not pack your app.');
      setStatus('error');
    }
  };

  return (
    <div
      className="bl-ship__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bl-ship"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <header className="bl-ship__header">
          <h2 id={titleId} className="bl-ship__title">
            Ship {projectName} to your phone
          </h2>
          <button type="button" className="bl-ship__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {status !== 'downloaded' && (
          <>
            <p className="bl-ship__lead">
              We bundle your project into a small zip you can run anywhere — your phone, a
              friend&rsquo;s laptop, or a static-file host like GitHub Pages. Nothing is uploaded to
              a server.
            </p>
            <ol className="bl-ship__steps">
              <li>Click Download.</li>
              <li>Move the zip to your phone (AirDrop, email, Drive, or USB cable).</li>
              <li>Unzip it on your phone, then follow the steps for your device below.</li>
            </ol>
            <div className="bl-ship__actions">
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button onClick={() => void handleDownload()} disabled={status === 'downloading'}>
                {status === 'downloading' ? 'Packaging…' : 'Download the zip'}
              </Button>
            </div>
            {status === 'error' && (
              <p role="alert" className="bl-ship__error">
                {error}
              </p>
            )}
          </>
        )}

        {status === 'downloaded' && (
          <>
            <p className="bl-ship__downloaded" role="status">
              Got it. Now put it on your phone:
            </p>
            <div className="bl-ship__platforms">
              <PlatformCard platform="ios" highlighted={detectedPlatform === 'ios'} />
              <PlatformCard platform="android" highlighted={detectedPlatform === 'android'} />
              <PlatformCard platform="desktop" highlighted={detectedPlatform === 'desktop'} />
            </div>
            <div className="bl-ship__actions bl-ship__actions--end">
              <Button onClick={onClose} type="button">
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PlatformCard({
  platform,
  highlighted,
}: {
  platform: DetectedPlatform;
  highlighted: boolean;
}): JSX.Element {
  const meta = PLATFORM_META[platform];
  return (
    <article
      className={`bl-ship__platform${highlighted ? ' bl-ship__platform--highlighted' : ''}`}
      aria-label={`Install on ${meta.label}`}
    >
      <header className="bl-ship__platform-header">
        <span className="bl-ship__platform-glyph" aria-hidden="true">
          {meta.glyph}
        </span>
        <h3 className="bl-ship__platform-title">
          {meta.label}
          {highlighted && <span className="bl-ship__platform-badge">You&rsquo;re here</span>}
        </h3>
      </header>
      <ol className="bl-ship__platform-steps">
        {meta.steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
    </article>
  );
}

const PLATFORM_META: Record<DetectedPlatform, { label: string; glyph: string; steps: string[] }> = {
  ios: {
    label: 'iPhone or iPad',
    glyph: '',
    steps: [
      'Open the unzipped folder in Files.',
      'Tap index.html to open it in Safari.',
      'Tap Share at the bottom of the screen.',
      'Choose "Add to Home Screen". Done!',
    ],
  },
  android: {
    label: 'Android phone',
    glyph: '🤖',
    steps: [
      'Unzip the folder on your phone.',
      'Open index.html in Chrome.',
      'Open the menu (three dots) and pick "Install app".',
      'It lands on your home screen like a normal app.',
    ],
  },
  desktop: {
    label: 'Laptop or desktop',
    glyph: '💻',
    steps: [
      'Unzip the folder.',
      'Drag it to a static host like Netlify Drop or GitHub Pages.',
      'Or run a local server and open from any device on the same wifi.',
      'Or just double-click index.html in a modern browser.',
    ],
  },
};
